/**
 * Context Commands - Discovery and data access
 * 
 * These commands help AI and apps discover available data sources
 * and understand what context is available before building apps.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useEntityStore from '../../stores/entityStore';
import { entityService } from '../../services/entityService';
import { API_BASE_URL } from '../../api/config';

const log = createFileLogger('CONTEXT_COMMANDS');
export const contextDiscoverCommand: SelfContainedCommand = {
  id: 'context_discover',
  description: 'Discover available context and data sources for an entity type.',
  
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'intent',
        type: 'string',
        description: 'User intent describing what data they need (e.g., "bank transactions", "emails", "calendar events")',
        required: false,
        examples: [
          'bank transactions for expense tracking',
          'email data for inbox management',
          'calendar events for scheduling',
          'files and documents'
        ]
      },
      {
        name: 'entityTypes',
        type: 'array',
        description: 'Specific entity types to check (e.g., ["BankTransaction", "Email.Gmail"])',
        required: false
      },
      {
        name: 'includeSchema',
        type: 'boolean',
        description: 'Whether to include schema definitions for entity types',
        required: false,
        defaultValue: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { intent: 'bank transactions' },
        expectedOutput: createSuccessResult('Plaid Banking integration needed', {
          suggestedIntegration: { type: 'Plaid', name: 'Plaid Banking' }
        }),
        description: 'Discover banking data needs',
        context: 'Before building expense tracker app'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { intent, entityTypes, includeSchema = false } = params;
    
    try {
      log.info('[context_discover] Discovering context:', { intent, entityTypes });
      
      // Get list of connected integrations
      const integrations = await getConnectedIntegrations();
      
      // Get entity types that have actual data
      const availableTypes = await getAvailableEntityTypes();
      
      // Get schemas if requested
      let schemas: any = undefined;
      if (includeSchema && entityTypes && entityTypes.length > 0) {
        schemas = await getEntitySchemas(entityTypes);
      }
      
      // Simple mapping of data intent to integrations (v1 - no LLM)
      let suggestedIntegration = null;
      if (intent) {
        suggestedIntegration = getIntegrationForIntent(intent, integrations);
      }
      
      const result = {
        // What's available now
        hasData: availableTypes.length > 0,
        connectedIntegrations: integrations.filter(i => i.status === 'connected'),
        availableEntityTypes: availableTypes,
        
        // What's needed (simple mapping)
        suggestedIntegration: suggestedIntegration,
        
        // Schema info (if requested)
        schemas: schemas,
        
        // Summary
        summary: {
          totalIntegrations: integrations.length,
          connectedCount: integrations.filter(i => i.status === 'connected').length,
          entityTypesWithData: availableTypes.length
        }
      };
      
      const message = suggestedIntegration && !suggestedIntegration.isConnected
        ? `${suggestedIntegration.name} integration needed for: "${intent}"`
        : `Discovered ${availableTypes.length} entity types with data`;
      
      return createSuccessResult(message, result, {
        format: 'json',
        metadata: {
          executionTime: Date.now() - startTime,
          method: 'context_discovery'
        } as any
      });
      
    } catch (error: any) {
      log.error('[context_discover] Error:', error);
      return createErrorResult(
        'Context discovery failed',
        `Failed to discover context: ${error.message}`,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * List all integrations (connected and available)
 */
export const integrationListCommand: SelfContainedCommand = {
  id: 'integration_list',
  description: `List all integrations (connected and available).

Returns information about:
- Currently connected integrations
- Available integrations that can be set up
- Integration capabilities and status

**EXAMPLES:**

List all integrations:
{}

Returns:
{
  "integrations": [
    {
      "type": "Gmail",
      "name": "Gmail",
      "status": "connected",
      "capabilities": ["email_read", "email_send"],
      "entityTypesProvided": ["Email.Gmail"],
      "lastSync": "2026-02-17T10:30:00Z"
    },
    {
      "type": "Plaid",
      "name": "Plaid Banking",
      "status": "disconnected",
      "capabilities": ["transaction_sync"],
      "entityTypesProvided": ["BankTransaction"]
    }
  ]
}`,
  
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: []
  },
  
  examples: {
    good: [
      {
        input: {},
        expectedOutput: createSuccessResult('Found integrations', {
          integrations: []
        }),
        description: 'List all integrations',
        context: 'Check available integrations'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      const integrations = await getConnectedIntegrations();
      
      return createSuccessResult(
        `Found ${integrations.length} integrations`,
        { integrations },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            count: integrations.length
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[integration_list] Error:', error);
      return createErrorResult(
        'Failed to list integrations',
        error.message,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * Smart data query - checks integration availability first
 */
export const dataQueryCommand: SelfContainedCommand = {
  id: 'data_query',
  description: `Smart data query with automatic integration checking.

This is a high-level wrapper around entity_find that:
1. Maps data types to entity types
2. Checks if required integration is connected
3. Returns helpful error if integration is missing
4. Queries entities if integration is available

**DATA TYPES:**
- bank_transactions → BankTransaction (requires Plaid)
- emails → Email.Gmail (requires Gmail)
- calendar_events → CalendarEvent (requires Google Calendar)
- files → File (requires LocalFiles/GoogleDrive/Dropbox)

**IF INTEGRATION MISSING:**
When this tool returns an error about missing integration:
1. Call setup_integration with the service name from the error
2. DO NOT try to query again until user sets up the integration
3. The setup_integration tool will show a button for the user

**EXAMPLES:**

Query bank transactions:
{
  "dataType": "bank_transactions",
  "filters": { "skeleton.amount": { "$gt": 1000 } }
}

If Plaid not connected, then call:
{
  "integrationType": "oauth",
  "serviceName": "Plaid",
  "purpose": "To query bank transaction data"
}`,
  
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'dataType',
        type: 'string',
        description: 'Type of data to query: bank_transactions | emails | calendar_events | files',
        required: true,
        examples: ['bank_transactions', 'emails', 'calendar_events', 'files']
      },
      {
        name: 'filters',
        type: 'object',
        description: 'Filter criteria (passed to entity_find)',
        required: false
      },
      {
        name: 'searchQuery',
        type: 'string',
        description: 'Search query (passed to entity_find)',
        required: false
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum results',
        required: false,
        defaultValue: 50
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { dataType: 'bank_transactions' },
        expectedOutput: createSuccessResult('Found bank transactions', {
          entities: [],
          count: 0
        }),
        description: 'Query bank transactions',
        context: 'Get transaction data for app'
      }
    ],
    bad: [
      {
        input: { dataType: 'invalid' },
        error: 'Unknown data type',
        why: 'Data type not supported',
        fix: 'Use valid data type'
      }
    ]
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { dataType, filters, searchQuery, limit = 50 } = params;
    
    try {
      // Map data type to entity type and required integration
      const dataTypeMap: Record<string, { entityType: string; integration: string; integrationName: string }> = {
        'bank_transactions': {
          entityType: 'BankTransaction',
          integration: 'Plaid',
          integrationName: 'Plaid Banking'
        },
        'emails': {
          entityType: 'Email.Gmail',
          integration: 'Gmail',
          integrationName: 'Gmail'
        },
        'calendar_events': {
          entityType: 'CalendarEvent',
          integration: 'GoogleCalendar',
          integrationName: 'Google Calendar'
        },
        'files': {
          entityType: 'File',
          integration: 'LocalFiles', // or GoogleDrive, Dropbox
          integrationName: 'File Storage'
        }
      };
      
      const mapping = dataTypeMap[dataType];
      if (!mapping) {
        return createErrorResult(
          `Unknown data type: ${dataType}`,
          `Supported types: ${Object.keys(dataTypeMap).join(', ')}`,
          'validation'
        );
      }
      
      // Check if integration is connected
      const integrations = await getConnectedIntegrations();
      const integration = integrations.find(i => i.type === mapping.integration && i.status === 'connected');
      
      if (!integration) {
        return createErrorResult(
          `Integration not connected: ${mapping.integrationName}`,
          `To query ${dataType}, you need to connect ${mapping.integrationName}. Run context_discover for setup instructions.`,
          'validation',
          {
            data: {
              dataType,
              requiredIntegration: mapping.integration,
              integrationName: mapping.integrationName,
              suggestion: `Connect ${mapping.integrationName} integration first`
            }
          }
        );
      }
      
      // Integration connected - query entities using entity_find
      const { useCommandRegistryStore } = await import('../../stores/commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      
      const findResult = await executeCommand('entity_find', {
        entityType: mapping.entityType,
        filters,
        searchQuery,
        limit
      }) as StandardToolResult;
      
      if (findResult.status !== 'success') {
        return findResult;
      }
      
      return createSuccessResult(
        `Found ${findResult.data.count} ${dataType}`,
        {
          ...findResult.data,
          dataType,
          integration: {
            type: integration.type,
            name: integration.name,
            lastSync: integration.lastSync
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            dataType,
            count: findResult.data.count
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[data_query] Error:', error);
      return createErrorResult(
        'Data query failed',
        error.message,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

async function getConnectedIntegrations(): Promise<any[]> {
  // Query integration entities from store
  const entityStore = useEntityStore.getState();
  const integrationEntities = entityStore.entities.filter(e => 
    e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')
  );
  const apiConnectionEntities = entityStore.entities.filter(e => e.entityType === 'APIConnection');
  
  log.info('[getConnectedIntegrations] Found entities:', {
    integrationCount: integrationEntities.length,
    integrationTypes: integrationEntities.map(e => (e as any).skeleton?.integrationType),
    apiConnectionCount: apiConnectionEntities.length
  });
  
  // Available IntegrationSystem definitions
  const definitions = [
    {
      type: 'Gmail',
      name: 'Gmail',
      capabilities: ['email_read', 'email_send'],
      entityTypesProvided: ['Email.Gmail']
    },
    {
      type: 'Plaid',
      name: 'Plaid Banking',
      capabilities: ['transaction_sync'],
      entityTypesProvided: ['BankTransaction']
    },
    {
      type: 'GoogleCalendar',
      name: 'Google Calendar',
      capabilities: ['calendar_read', 'calendar_write'],
      entityTypesProvided: ['CalendarEvent']
    },
    {
      type: 'GoogleDrive',
      name: 'Google Drive',
      capabilities: ['file_read', 'file_write'],
      entityTypesProvided: ['File.GoogleDrive']
    },
    {
      type: 'GoogleAnalytics',
      name: 'Google Analytics',
      capabilities: ['analytics_query'],
      entityTypesProvided: []
    },
    {
      type: 'GoogleAds',
      name: 'Google Ads',
      capabilities: ['ads_query', 'ads_mutate'],
      entityTypesProvided: []
    },
    {
      type: 'LocalFiles',
      name: 'Local Files',
      capabilities: ['file_read', 'file_watch'],
      entityTypesProvided: ['File.Local']
    },
    {
      type: 'Dropbox',
      name: 'Dropbox',
      capabilities: ['file_read', 'file_sync'],
      entityTypesProvided: ['File.Dropbox']
    }
  ];
  
  // NEW: Add managed APIs as always available
  const managedAPIs = [
    {
      type: 'CJDropshipping',
      name: 'CJDropshipping API',
      status: 'available',
      managedApiId: 'managed_cjdropshipping',
      isManaged: true,
      autoEnable: true,
      isAPIConnection: true,
      pricing: {
        flatRate: 10,
        billing: 'credits_plus_invoice',
        description: '10 credits per API call + invoice'
      },
      capabilities: ['product_search', 'order_fulfillment', 'inventory_check'],
      entityTypesProvided: ['Product'],
      description: 'Product sourcing and fulfillment (500K+ products)'
    },
  ];
  
  // Check if user has enabled any managed APIs (update status)
  for (const managedAPI of managedAPIs) {
    const userConnection = apiConnectionEntities.find((conn: any) => 
      conn.skeleton?.managedApiId === managedAPI.managedApiId
    );
    
    if (userConnection) {
      managedAPI.status = (userConnection as any).skeleton?.autoEnabled ? 'auto_enabled' : 'connected';
      (managedAPI as any).entityId = userConnection._id;
    }
  }
  
  // Merge connected status from actual Integration entities
  const mergedIntegrations = definitions.map(def => {
    const connectedEntity = integrationEntities.find(
      e => (e as any).skeleton?.integrationType === def.type
    );
    
    return {
      ...def,
      status: connectedEntity ? (connectedEntity as any).skeleton?.status || 'connected' : 'available',
      lastSync: connectedEntity ? (connectedEntity as any).skeleton?.lastSyncAt : undefined,
      entityId: connectedEntity?._id,
      entityName: connectedEntity?.name
    };
  });
  
  // Add APIConnection entities
  const apiConnections = apiConnectionEntities
    .filter(e => (e as any).skeleton?.status !== 'inactive')
    .map(conn => {
      const skeleton = (conn as any).skeleton || {};
      return {
        type: skeleton.serviceName || conn.name,
        name: conn.name,
        capabilities: ['api_connect'],
        entityTypesProvided: [],
        status: skeleton.status || 'active',
        lastSync: skeleton.lastUsedAt,
        entityId: conn._id,
        entityName: conn.name,
        category: skeleton.category,
        isAPIConnection: true
      };
    });
  
  const all = [...mergedIntegrations, ...apiConnections, ...managedAPIs];
  
  log.info('[getConnectedIntegrations] Merged result:', {
    total: all.length,
    integrationSystemCount: mergedIntegrations.length,
    apiConnectionCount: apiConnections.length,
    managedAPICount: managedAPIs.length,
    connected: all.filter(i => i.status === 'connected' || i.status === 'active').length
  });
  
  return all;
}

async function getAvailableEntityTypes(): Promise<any[]> {
  // Call backend API to get entity type stats
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/entities/types/stats`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    // Fallback: Calculate from entity store
    const entityStore = useEntityStore.getState();
    const typeMap = new Map<string, { count: number; lastUpdated: string }>();
    
    entityStore.entities.forEach(e => {
      const existing = typeMap.get(e.entityType) || { count: 0, lastUpdated: e.updated_at };
      typeMap.set(e.entityType, {
        count: existing.count + 1,
        lastUpdated: e.updated_at > existing.lastUpdated ? e.updated_at : existing.lastUpdated
      });
    });
    
    return Array.from(typeMap.entries()).map(([entityType, stats]) => ({
      entityType,
      count: stats.count,
      lastUpdated: stats.lastUpdated,
      source: 'unknown'
    }));
  }
}

/**
 * Simple keyword matching to suggest integration (v1 - no LLM)
 */
function getIntegrationForIntent(intent: string, currentIntegrations: any[]): any | null {
  const intentLower = intent.toLowerCase();
  
  // Bank transactions
  if (intentLower.includes('bank') || intentLower.includes('transaction') || 
      intentLower.includes('expense') || intentLower.includes('payment')) {
    const plaid = currentIntegrations.find(i => i.type === 'Plaid');
    return {
      type: 'Plaid',
      name: 'Plaid Banking',
      reason: 'Provides bank transaction data',
      setupCommand: 'connect_integration',
      setupParams: { integration: 'plaid' },
      isConnected: plaid?.status === 'connected',
      entityTypesProvided: ['BankTransaction']
    };
  }
  
  // Emails
  if (intentLower.includes('email') || intentLower.includes('inbox') || 
      intentLower.includes('message') || intentLower.includes('gmail')) {
    const gmail = currentIntegrations.find(i => i.type === 'Gmail');
    return {
      type: 'Gmail',
      name: 'Gmail',
      reason: 'Provides email access',
      setupCommand: 'connect_integration',
      setupParams: { integration: 'gmail' },
      isConnected: gmail?.status === 'connected',
      entityTypesProvided: ['Email.Gmail']
    };
  }
  
  // Calendar
  if (intentLower.includes('calendar') || intentLower.includes('event') || 
      intentLower.includes('meeting') || intentLower.includes('schedule')) {
    const calendar = currentIntegrations.find(i => i.type === 'GoogleCalendar');
    return {
      type: 'GoogleCalendar',
      name: 'Google Calendar',
      reason: 'Provides calendar and event data',
      setupCommand: 'connect_integration',
      setupParams: { integration: 'google_calendar' },
      isConnected: calendar?.status === 'connected',
      entityTypesProvided: ['CalendarEvent']
    };
  }
  
  return null;
}

async function getEntitySchemas(entityTypes: string[]): Promise<Record<string, any>> {
  // In production, this would fetch from backend
  // For now, return placeholder schemas
  const schemas: Record<string, any> = {};
  
  for (const type of entityTypes) {
    schemas[type] = {
      type,
      properties: {
        name: { type: 'string', required: true },
        skeleton: { type: 'object', required: true }
      },
      example: `Schema for ${type} would be here`
    };
  }
  
  return schemas;
}

export const contextCommands = [
  contextDiscoverCommand,
  integrationListCommand,
  dataQueryCommand
];
